import axios, { AxiosError, AxiosResponse } from 'axios'
import idx from 'idx'
import { destroy, types } from 'mobx-state-tree'
import Player from '../components/Player'
import { GASendEvent } from '../ga'
import { IPlayerStore, PlayerState, PlayerStore } from './player'
import {
  IScript,
  IScriptStore,
  ISequenceEvent,
  ISequenceItem,
  ScriptState,
  ScriptStore,
} from './script'

export const TopLevelError = types.model('TopLevelError', {
  hasFix: false,
  text: types.string,
})

export type ITopLevelError = typeof TopLevelError.Type

export const RootStore: any = types
  .model('RootStore', {
    player: PlayerStore,
    script: ScriptStore,
    topLevelError: types.maybe(TopLevelError),
  })
  .views(self => ({
    get activeCard() {
      if (!self.script.data) {
        return null
      }

      if (self.script.data.sequence.length > self.player.cardIndex) {
        return self.script.data.sequence[self.player.cardIndex]
      }
    },
    get visibleCards() {
      if (!self.script.data) {
        return []
      }

      return self.script.data.sequence.slice(self.player.cardIndex)
    },
  }))
  .views(self => ({
    get activeEvent() {
      if (!self.script.data || !self.activeCard) {
        return null
      }

      const event = self.activeCard.events[self.player.eventIndex]
      return event
    },
    get nextEventInCard() {
      // Check if next event is in current card
      if (self.activeCard) {
        const nextEventExists =
          self.player.eventIndex + 1 < self.activeCard.events.length

        if (nextEventExists) {
          return self.activeCard.events[self.player.eventIndex + 1]
        }
      }

      return null
    },
    get prevCard() {
      if (self.script.data) {
        const prevCardExists =
          self.player.cardIndex > 0 &&
          self.script.data.sequence[self.player.cardIndex - 1]

        if (prevCardExists) {
          return self.script.data.sequence[self.player.cardIndex - 1]
        }
      }

      return null
    },
    get nextCard() {
      // Check if next card's event exists
      if (self.script.data) {
        const nextCardExists =
          self.player.cardIndex + 1 < self.script.data.sequence.length

        if (nextCardExists) {
          return self.script.data.sequence[self.player.cardIndex + 1]
        }
      }

      return null
    },
  }))
  .actions((self: typeof RootStore.Type) => {
    // Store a callback to any thrown errors
    let topLevelErrorFixCallback: (() => Promise<void>) | null

    const actions = {
      resetModule() {
        self.script.reset()
        self.player.reset()
      },
      loadModule(script: IScript) {
        self.script.data = script
        self.script.state = ScriptState.loaded
      },
      throwTopLevelError(error: string, fixCallback?: () => Promise<void>) {
        if (!error) {
          throw new Error('Invalid arguments: no error')
        }

        if (fixCallback) {
          topLevelErrorFixCallback = fixCallback
        }

        GASendEvent('error', 'throw', error)

        self.topLevelError = TopLevelError.create({
          hasFix: fixCallback ? true : false,
          text: error,
        })
      },
      destroyTopLevelError() {
        destroy(self.topLevelError)
      },
      tryTopLevelErrorFix() {
        return new Promise((resolve, reject) => {
          if (!topLevelErrorFixCallback) {
            self.throwTopLevelError(
              'RootStore failed to call error fix callback, as it is not defined'
            )
            resolve()
            return
          }
  
          const originalError = self.topLevelError.text

          topLevelErrorFixCallback()
            .then(() => {
              self.destroyTopLevelError()
              resolve()
            })
            .catch((error) => {
              self.destroyTopLevelError()
              self.throwTopLevelError(
                `We tried, but failed to automatically fix this error: ${originalError}`
              )
              reject()
            })
          
          topLevelErrorFixCallback = null
        })
      },
      undoCompleteEvent() {
        self.player.state = PlayerState.ended
        self.player.progressPercentage = 0

        if (self.prevCard) {
          self.player.cardIndex -= 1
        }
      },
      completeEvent() {
        self.player.state = PlayerState.ended
        self.player.progressPercentage = 0

        GASendEvent(
          'module',
          'cardend',
          self.script.data.shortname,
          self.player.cardIndex
        )

        if (self.nextEventInCard) {
          self.player.eventIndex += 1
          return
        }

        if (self.nextCard) {
          self.player.cardIndex += 1
          self.player.eventIndex = 0
          return
        }

        // No more events or cards available
        self.player.cardIndex += 1
        self.player.eventIndex = 0
        self.player.state = PlayerState.completed

        GASendEvent('module', 'completed', self.script.data.shortname)
      },
    }

    return actions
  })

export interface IRootStore {
  player: IPlayerStore
  script: IScriptStore
  topLevelError: ITopLevelError

  // Actions
  resetModule: () => void
  loadModule: (script: IScript) => void
  throwTopLevelError: (error: string, fixCallback?: () => Promise<void>) => void
  tryTopLevelErrorFix: () => void
  undoCompleteEvent: () => void
  completeEvent: () => void

  // Views
  activeCard?: ISequenceItem
  visibleCards: ISequenceItem[]
  activeEvent?: ISequenceEvent
}



// WEBPACK FOOTER //
// ./src/models/root.ts